import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, Check } from "lucide-react";
import axios from "axios";
import { Wordmark, GoldRule } from "@/components/Brand";

const API = process.env.REACT_APP_BACKEND_URL;

function isStrongEnough(pw) {
    return pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);
}

export function StaffResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const strong = useMemo(() => isStrongEnough(pw), [pw]);
    const match = useMemo(() => pw && pw === confirm, [pw, confirm]);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!token) { toast.error("Reset link is missing a token."); return; }
        if (!strong) { toast.error("Password must be at least 8 characters and include letters and numbers."); return; }
        if (!match) { toast.error("Passwords do not match."); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/staff/auth/reset`, { token, new_password: pw });
            setDone(true);
            toast.success("Password updated");
            setTimeout(() => nav("/staff/login", { replace: true }), 2000);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "This reset link is invalid or has expired.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[100dvh] flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md" data-testid="staff-reset-password-page">
                <div className="flex justify-center"><Wordmark /></div>
                <GoldRule className="mt-6" />
                <div className="mt-10 rounded-3xl glass p-8 space-y-6">
                    <div className="text-center">
                        <div className="mx-auto h-14 w-14 rounded-full glass flex items-center justify-center border-gold/50 border">
                            <KeyRound className="h-5 w-5 text-gold" strokeWidth={1.5} />
                        </div>
                        <h1 className="mt-6 font-serif font-light text-3xl text-white">
                            Choose a new <span className="gold-text">password</span>
                        </h1>
                        <p className="mt-2 text-sm text-white/60">Your new password must be at least 8 characters and include letters and numbers.</p>
                    </div>

                    {!token ? (
                        <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-100" data-testid="reset-no-token">
                            This reset link is missing its token. Please request a new reset from the sign-in page.
                        </div>
                    ) : done ? (
                        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-100 inline-flex items-center gap-2" data-testid="reset-done">
                            <Check className="h-4 w-4" /> Password updated. Redirecting to sign in…
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-6">
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mb-2 font-semibold">New password</div>
                                <input
                                    data-testid="reset-password"
                                    type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                                    autoComplete="new-password" required minLength={8}
                                    className="w-full font-ui text-base"
                                />
                                {pw && !strong && (
                                    <div className="mt-1 text-[11px] text-amber-200">
                                        Must be at least 8 characters and include letters and numbers.
                                    </div>
                                )}
                            </label>
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mb-2 font-semibold">Confirm password</div>
                                <input
                                    data-testid="reset-confirm"
                                    type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                    autoComplete="new-password" required
                                    className="w-full font-ui text-base"
                                />
                                {confirm && !match && (
                                    <div className="mt-1 text-[11px] text-amber-200">Passwords do not match.</div>
                                )}
                            </label>
                            <button
                                type="submit"
                                disabled={submitting || !strong || !match}
                                data-testid="reset-submit"
                                className="w-full h-14 rounded-full bg-gold text-navy font-ui font-semibold text-base tracking-wide shadow-[0_10px_30px_-8px_rgba(197,160,89,0.55)] hover:bg-gold-light active:scale-[0.98] transition-all disabled:opacity-40"
                            >
                                {submitting ? "Updating…" : "Update password"}
                            </button>
                        </form>
                    )}

                    <div className="text-center">
                        <Link to="/staff/login" data-testid="reset-back-link"
                              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-gold transition-colors">
                            <ArrowLeft className="h-3 w-3" /> Back to sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
